import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OnboardingShell } from '../../components/onboarding/OnboardingShell';
import { usePatchMePrefs } from '../../hooks/usePatchMePrefs';
import type { LanguageCode } from '../../hooks/useMe';
import styles from './LanguagePick.module.css';

/**
 * Onboarding → Language pick (first interactive step, between Splash and AgePrivacy).
 *
 * Shown BEFORE the user has chosen a language, so the screen is intentionally
 * tri-lingual / neutral (no i18n keys for its own copy) — mirrors the bot's
 * `birthDataStep` picker. Selecting a locale:
 *   1. flips the UI immediately via i18n.changeLanguage
 *   2. persists to the User row via PATCH /me { language } (so the bot uses it too)
 *   3. advances to /onboarding/age
 *
 * This is a CLIENT-ONLY step: it is not part of the server `onboardingStep`
 * machine (Splash=0, AgeGate=1, …), so resume-on-refresh is unaffected — a
 * returning mid-onboarding user lands on their stored step and skips this once
 * their language is already set. Splash routes here instead of straight to /age.
 */

interface LangOption {
  code: LanguageCode;
  flag: string;
  name: string;
}

// Order: ru, uk, en — matches the bot's birthDataStep picker.
const OPTIONS: LangOption[] = [
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'uk', flag: '🇺🇦', name: 'Українська' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
];

export function LanguagePick(): JSX.Element {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const patch = usePatchMePrefs();
  const [picked, setPicked] = useState<LanguageCode | null>(null);

  const onPick = (code: LanguageCode): void => {
    if (picked) return;
    setPicked(code);
    void i18n.changeLanguage(code);
    // Optimistic mutation (updates the ['me'] cache); survives unmount so we can
    // navigate immediately without waiting for the round-trip.
    patch.mutate({ language: code });
    navigate('/onboarding/age', { replace: true });
  };

  return (
    <OnboardingShell>
      <div className={styles.center}>
        <div className={styles.globe} aria-hidden="true">🌐</div>
        <h1 className={styles.title}>
          Выбери язык<br />Choose language<br />Оберіть мову
        </h1>
        <div className={styles.options} role="radiogroup" aria-label="Language / Язык / Мова">
          {OPTIONS.map((o) => (
            <button
              key={o.code}
              type="button"
              role="radio"
              aria-checked={picked === o.code}
              className={styles.option}
              data-selected={picked === o.code}
              disabled={picked !== null}
              onClick={() => onPick(o.code)}
              data-testid={`onboarding-language-${o.code}`}
            >
              <span className={styles.flag}>{o.flag}</span>
              <span className={styles.name}>{o.name}</span>
            </button>
          ))}
        </div>
      </div>
    </OnboardingShell>
  );
}
